import { type Signal, useMonitor } from "@suse-o11y/signal";
import React, { useState } from "react";
import { useMeasure } from "react-use";
import { z } from "zod";

import { Button } from "~/components/button";
import { IconArrowDownRounded, IconLeftOutlined } from "~/components/icon";
import type { NodeId } from "~/models/dto";
import { cx } from "~/tools/class-variance";
import { LocalStorage, MONITOR_STATUS_DRAWER_SCROLL_INDICATOR } from "~/tools/local-storage";

import type { TimeRange } from "../../../../../app/state/view/timeline/TimeRange";
import { isHealthStateUnHealthy } from "../../../../../common/functions";
import type { MonitorCheckStatus } from "../../../../../common/openapi";
import { ComponentBoundary } from "../../../../../components/_component-boundary";
import {
	CloseDrawerButton,
	DrawerComponentTitle,
	ExplorationDrawer,
} from "../../../../../components/react/drawer/ExplorationDrawer";
import { LinkTo } from "../../../../../components/react/link";
import { LoadingSpinner } from "../../../../../components/react/loading-spinner/LoadingSpinner";
import { NotFound } from "../../../../../components/react/not-found/NotFound";
import type { SimpleStatefulComponent } from "../../../../../components/react/states/state-component-utils";
import { Tooltip } from "../../../../../components/react/tooltip/Tooltip";
import { queryMonitorCheckStatus } from "../../../../../queries";
import { useQuery } from "../../../../../queries/react-query";
import { COMPONENT_PATH } from "../../../../../routes/Routes";
import { usePinnedItemsState } from "../../../panes/east/pinned-items";
import type { PinnedItemsState } from "../../../panes/east/pinned-items/PinnedItemsState";
import { PinningButton } from "../../../panes/east/pinned-items/PinningButton";
import { MonitorHealthTimeline } from "../MonitorHealthTimeline";
import { MonitorIntro } from "../MonitorIntro";
import { MonitorRemediationTabs } from "../MonitorRemediationTabs";
import { MonitorStatusDrawerHealthState } from "../MonitorStatusDrawerHealthState";
import { MonitorStatusDrawerTabs } from "../MonitorStatusDrawerTabs";

import { MonitorStatusDrawerProvider } from "./monitor-status-drawer-provider";
import type { MonitorStatusDrawerState } from "./monitor-status-drawer-state";

export interface MonitorStatusDrawerProps {
	when: undefined | MonitorStatusDrawerState;
}

export const MonitorStatusDrawer: React.FC<MonitorStatusDrawerProps> = ({ when }) => {
	const monitor = useMonitor();
	const pinnedItemsState = usePinnedItemsState();
	const scrollRef = React.useRef<HTMLDivElement>(null);
	const [contentRef, { height: contentHeight }] = useMeasure<HTMLDivElement>();

	const checkState = when?.checkStateChain.peek(monitor);

	const [cachedData, setCachedData] = useState<MonitorCheckStatus>();
	const {
		data = cachedData?.identifier === checkState?.identifier ? cachedData : undefined,
		isFetching,
		error,
	} = useQuery({
		keepPreviousData: cachedData?.identifier === checkState?.identifier,
		enabled: checkState != null,
		onSuccess: (responseData) => setCachedData(responseData),
		...queryMonitorCheckStatus({
			identifier: checkState?.identifier ?? "",
			timestamp: when?.timestamp.read(monitor),
		}),
	});

	const component: undefined | SimpleStatefulComponent = data && {
		id: data.component.id as NodeId,
		name: data.component.name,
		identifiers: [data.component.identifier],
		typeName: data.component.type,
		icon: data.component.icon,
		healthState: data.health,
	};

	React.useEffect(() => {
		if (checkState != null) {
			scrollRef.current?.scrollTo(0, 0);
		}
	}, [checkState]);

	const scrollDownIndicatorVisible =
		LocalStorage.getItem(monitor, MONITOR_STATUS_DRAWER_SCROLL_INDICATOR, z.boolean()) ?? true;

	const showScrollIndicator =
		scrollDownIndicatorVisible && scrollRef.current != null && scrollRef.current.clientHeight < contentHeight;

	return (
		<ExplorationDrawer
			className="flex flex-col space-y-4"
			data-testid="monitor-status-drawer"
			footer={
				!isFetching && (
					<>
						<CloseDrawerButton />
						{data && (
							<LinkTo
								params={{
									identifier: data.component.identifier,
								}}
								search={(search) => ({
									...search,
									query: undefined,
									explore: undefined,
								})}
								to={COMPONENT_PATH}
							>
								<CloseDrawerButton variant="primary">View component</CloseDrawerButton>
							</LinkTo>
						)}
					</>
				)
			}
			headerActions={({ checkStateChain, timeRange }) => (
				<>
					{checkStateChain.size(monitor) > 1 && (
						<Button className="gap-2" onClick={() => checkStateChain.pop()} variant="tertiary">
							<IconLeftOutlined className="text-xs" />
							Previous
						</Button>
					)}
					{!isFetching && data && <PinningButton item={{ ...data, timeRange }} />}
				</>
			)}
			headerTitle={
				<DrawerComponentTitle
					component={component}
					displayEmptyStateComponent={!isFetching}
					displayTypeName
					typeOfData="Monitor status"
				/>
			}
			onScroll={() => {
				LocalStorage.setItem(MONITOR_STATUS_DRAWER_SCROLL_INDICATOR, false);
			}}
			ref={scrollRef}
			when={when}
		>
			{({ timestamp, timeRange, checkStateChain }, close) => (
				<ComponentBoundary component={data?.component}>
					<MonitorStatusDrawerProvider timeRange={timeRange} timestamp={timestamp}>
						<div className="space-y-4" ref={contentRef}>
							{data && <MonitorIntro monitorStatus={data} />}
							{!isFetching && checkState && (
								<MonitorHealthTimeline disabled={isFetching} identifier={checkState.identifier} />
							)}

							{isFetching && <LoadingSpinner className="h-full" size="large" />}

							{!isFetching && error != null && (
								<NotFound header="No data found">
									Either the component or the observed health state does not exist at the selected time.
									<br />
									Choose a different time by clicking the monitor health timeline in a non-grey area.
								</NotFound>
							)}

							{!(isFetching || error) && data && (
								<>
									<MonitorStatusDrawerHealthState
										health={data.health}
										reason={data.reason}
										selectedTimestamp={data.topologyTime}
										triggeredTimeStamp={data.triggeredTimestamp}
									/>

									<div>
										<MonitorStatusDrawerTabs checkState={data} checkStateChain={checkStateChain} />
									</div>

									{isHealthStateUnHealthy(data.health) && (
										<MonitorRemediationSection
											close={close}
											data={data}
											pinnedItemsState={pinnedItemsState}
											timeRange={timeRange}
										/>
									)}
								</>
							)}

							<ScrollIndicator show={showScrollIndicator} />
						</div>
					</MonitorStatusDrawerProvider>
				</ComponentBoundary>
			)}
		</ExplorationDrawer>
	);
};

const MonitorRemediationSection: React.FC<{
	data: MonitorCheckStatus;
	close: () => void;
	pinnedItemsState: PinnedItemsState;
	timeRange: Signal<TimeRange>;
}> = ({ data, close, pinnedItemsState, timeRange }) => {
	if (!data.troubleshootingSteps) {
		return null;
	}

	return (
		<MonitorRemediationTabs
			componentIdentifier={data.component.identifier}
			onLinkClick={(autoPinEnabled) => {
				if (autoPinEnabled) {
					pinnedItemsState.pin({
						...data,
						component: data.component,
						timeRange,
					});
				}
				close();
			}}
			text={data.troubleshootingSteps}
			timestamp={data.topologyTime}
		/>
	);
};

const ScrollIndicator: React.FC<{ show: boolean }> = ({ show }) => (
	<Tooltip
		className={cx(
			"absolute inset-x-1/2 bottom-[72px] z-10 flex h-[72px] w-10 -translate-x-1/2 flex-col items-center justify-center gap-3 rounded-[30px] bg-grey-800 text-white transition-opacity duration-500",
			show ? "opacity-100" : "opacity-0",
		)}
		title="Scroll down"
		visible={show}
	>
		<div className="relative h-[22px] w-4 rounded-lg border border-white">
			<span className="absolute inset-x-1/2 top-[29%] h-1 w-1 -translate-x-1/2 animate-move rounded-full bg-white" />
		</div>
		<IconArrowDownRounded className="text-[8px]" />
	</Tooltip>
);
